import { useEffect, useState, useCallback, useMemo, useRef } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { formatEther, parseUnits } from 'viem';
import { ADDRESSES, CONFIG, CHAIN_ID } from '../config';
import { useAccount, useContractRead, useContractWrite, usePrepareContractWrite, useNetwork, usePublicClient, useWalletClient } from 'wagmi';
import { Abi } from 'viem';
import { Pencil, Tag, Info, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { formatTokenAmount } from '../utils/formatting';
import { AssetsList } from './AssetsList';
import { BondActions } from './BondActions';
import { useEntityDetails } from '../hooks/useEntityDetails';
import { useNavigate } from 'react-router-dom';
import { EntityCard } from './EntityCard';
import { ProductCard } from './ProductCard';
import { EntityDetails } from '../hooks/useEntityDetails';
import { processImageData } from '../utils/ipfsUtils';

// Add ProductStatus enum
enum ProductStatus {
  Pending,
  Active,
  Redeemed,
  Reclaimed,
  Inactive,
  Expired,
  Created,
  Transferred,
  Reselling,
  Purchased
}

// Bond interface aligned with contract's Bond struct
interface Bond {
  id: number;
  owner: string;
  asset: string;
  amount: bigint;
  createdAt: bigint;
  returnPercentage: number;
  termInSeconds: number;
  status: number;
  name: string;
  resellPrice: bigint;
  redemptionFee: number;
  reclaimFee: number;
  productAddress: `0x${string}`;
  productName: string;
}

// Update event type definitions
interface BondUpdatedEvent {
  args: {
    bondId: bigint;
    owner: string;
    amount: bigint;
    payout: bigint;
    returnPercentage: number;
    termInSeconds: number;
    status: number;
  };
}

interface BondTransferredEvent {
  args: {
    bondId: bigint;
    oldOwner: string;
    newOwner: string;
  };
}

interface ItemUpdatedEvent {
  args: {
    id: bigint;
    newowner: string;
    status: number;
  };
}

// Update BondDetailsResult type to match contract
type BondDetailsResult = [
  bigint,   // id
  string,   // owner
  string,   // asset
  bigint,   // amount
  bigint,   // createdAt
  bigint,   // returnPercentage
  bigint,   // termInSeconds
  number,   // status
  string,   // name
  bigint,   // resellPrice
  bigint,   // redemptionPeriod
  number,   // redemptionFee
  number,   // latePenalty
  number,   // trinityMasterServiceFee
  bigint,   // trinityFee
  number    // reclaimFee
];

type ProductResult = [
  string, // name
  string, // description
  string, // owner
  string, // treasurer
  string, // trinity token
  string, // coupon token
  string, // trinity fee
  string, // reclaim fee
  string, // redemption fee
  string, // late penalty
  string[], // terms
  string[], // term durations
  string[], // term return percentages
  string[], // term active states
];

// Entity Background Image Component
function EntityBackgroundImage({ entity }: { entity: EntityDetails }) {
  // Get entity properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: entity.address as `0x${string}`,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'properties',
    enabled: !!entity.address,
  }) as { data: `0x${string}` };

  // Get entity image from properties using getUIImage
  const { data: entityImageUrl } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getUIImage',
    args: [0], // Get the first image (index 0)
    enabled: !!propertiesAddress && !!entity.address,
  }) as { data: string };
  
  // Process the image data using the utility function
  const imageSrc = processImageData(entityImageUrl || '');

  if (!imageSrc) {
    return null;
  }

  return (
    <div 
      className="absolute inset-0 w-full h-full overflow-hidden"
      style={{
        backgroundImage: `url(${imageSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        filter: 'blur(10px) brightness(0.6)',
        transform: 'scale(1.05)', // Minimal scale to prevent edge bleeding
      }}
    />
  );
}

export function Accounts() {
  const { address } = useAccount();
  const { chain } = useNetwork();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { entities, isLoading: isLoadingEntities } = useEntityDetails();
  const [bonds, setBonds] = useState<Bond[]>([]);
  const [isLoadingBonds, setIsLoadingBonds] = useState(false);
  const [bondError, setBondError] = useState<Error | null>(null);
  const [selectedBondKey, setSelectedBondKey] = useState<string | null>(null);
  const [resellPrice, setResellPrice] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [newBondName, setNewBondName] = useState('');
  const [propertyKey, setPropertyKey] = useState('');
  const [propertyValue, setPropertyValue] = useState('');
  const [reclaimingBondIds] = useState<Set<number>>(new Set());
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isApprovingCoupon, setIsApprovingCoupon] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState<string | null>(null);
  const [selectedBondDetails, setSelectedBondDetails] = useState<any>(null);
  const [isLoadingBondDetails, setIsLoadingBondDetails] = useState(false);
  const [currentDescription, setCurrentDescription] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<`0x${string}` | undefined>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  // Check if user is admin
  const { data: ownerAddress } = useContractRead({
    address: ADDRESSES.TRINITY_BOND,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'owner',
  }) as { data: string | undefined };

  const isAdmin = address && ownerAddress && 
    address.toLowerCase() === (ownerAddress as string).toLowerCase();

  // Get coupon token balance
  const { data: couponBalance = 0n, refetch: refetchAllowance } = useContractRead({
    address: ADDRESSES.COUPON_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address && chain?.id === CHAIN_ID,
  }) as { data: bigint | undefined, refetch: () => Promise<any> };

  // Get coupon token allowance
  const { data: couponAllowance = 0n } = useContractRead({
    address: ADDRESSES.COUPON_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'allowance',
    args: [address, selectedBondKey ? bonds.find(b => b.id === Number(selectedBondKey.split('-')[1]))?.productAddress : ADDRESSES.TRINITY_BOND],
    enabled: !!address && chain?.id === CHAIN_ID && selectedBondKey !== null,
    watch: true,
  }) as { data: bigint | undefined };

  // Prepare coupon token approve
  const { config: approveCouponConfig } = usePrepareContractWrite({
    address: ADDRESSES.COUPON_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'approve',
    args: [selectedBondKey ? bonds.find(b => b.id === Number(selectedBondKey.split('-')[1]))?.productAddress : ADDRESSES.TRINITY_BOND, BigInt(1000000n * BigInt(1e18))],
    enabled: !!address && chain?.id === CHAIN_ID && couponAllowance === 0n,
  });

  const { write: approveCoupon } = useContractWrite({
    ...approveCouponConfig,
    onSuccess: () => {
      console.log('Coupon token approved');
      refetchAllowance();
      setIsApprovingCoupon(false);
    },
  });

  // Prepare redeem contract write
  const { config: redeemConfig } = usePrepareContractWrite({
    address: selectedBondKey ? bonds.find(b => b.id === Number(selectedBondKey.split('-')[1]))?.productAddress : ADDRESSES.TRINITY_BOND,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'redeemBond',
    args: selectedBondKey !== null ? [Number(selectedBondKey.split('-')[1])] : undefined,
    enabled: selectedBondKey !== null,
  });

  const { write: redeem } = useContractWrite({
    ...redeemConfig,
    onSuccess: () => {
      setSelectedBondKey(null);
      setIsRedeeming(false);
    },
  });

  // Prepare reclaim contract write
  const { config: reclaimConfig } = usePrepareContractWrite({
    address: selectedBondKey ? bonds.find(b => b.id === Number(selectedBondKey.split('-')[1]))?.productAddress : ADDRESSES.TRINITY_BOND,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'reclaimBond',
    args: selectedBondKey !== null ? [Number(selectedBondKey.split('-')[1])] : undefined,
    enabled: selectedBondKey !== null && couponAllowance > 0n,
  });

  const { write: reclaim } = useContractWrite({
    ...reclaimConfig,
    onSuccess: () => {
      setSelectedBondKey(null);
    },
  });

  // Memoize the filtered bond products to prevent unnecessary recalculations
  const bondProducts = useMemo(() => {
    if (!entities) return [];
    return entities.flatMap(entity => 
      entity.products.filter(product => product.className === 'TrinityBond')
    );
  }, [entities]);

  // Create a ref to store the fetchBonds function
  const fetchBondsRef = useRef<() => Promise<void>>();

  // Effect to refresh data periodically
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const refreshData = async () => {
      if (address && chain?.id === CHAIN_ID) {
        try {
          await Promise.all([refetchAllowance()]);
          // Set up the next refresh after success
          timeoutId = setTimeout(refreshData, 30000); // Increased to 30 seconds
        } catch (error) {
          console.error('Error refreshing data:', error);
          // On error, try again after a shorter delay
          timeoutId = setTimeout(refreshData, 15000); // Increased to 15 seconds
        }
      }
    };

    // Start the refresh cycle
    refreshData();

    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [address, chain?.id, refetchAllowance]);

  // Create the fetchBonds function
  fetchBondsRef.current = async () => {
    if (!address || chain?.id !== CHAIN_ID || !bondProducts.length) {
      console.log('Early return conditions:', {
        address,
        chainId: chain?.id,
        expectedChainId: CHAIN_ID,
        bondProductsLength: bondProducts.length
      });
      return;
    }
    
    setIsLoadingBonds(true);
    try {
      const processedBonds: Bond[] = [];

      console.log('Starting to process bond products:', bondProducts);

      // For each bond product
      for (const product of bondProducts) {
        try {
          console.log('Processing product:', {
            address: product.address,
            name: product.name
          });

          // Get BondUpdated events
          const updatedFilter = await publicClient.createContractEventFilter({
            address: product.address,
            abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
            eventName: 'BondUpdated',
            fromBlock: 'earliest',
            args: { owner: address }
          });

          const updatedEvents = (await publicClient.getFilterLogs({ filter: updatedFilter })) as unknown as BondUpdatedEvent[];
          console.log('Updated events:', updatedEvents);

          // Get user's items directly from the contract
          const userItems = await publicClient.readContract({
            address: product.address,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
            functionName: 'getOwnerItems',
            args: [address]
          }) as bigint[];

          console.log('User items from contract:', userItems);

          // Process events to build bond map
          const bondMap: { [key: number]: any } = {};

          // Process BondUpdated events
          updatedEvents.forEach(event => {
            const bondId = Number(event.args.bondId);
            bondMap[bondId] = {
              owner: event.args.owner,
              status: event.args.status
            };
            });

          // Process user's items
          userItems.forEach(itemId => {
            bondMap[Number(itemId)] = {
              owner: address,
              status: ProductStatus.Purchased
            };
          });

          // Fetch additional details for each bond
          for (const [bondId, bond] of Object.entries(bondMap)) {
            // Skip if not owned by current user
            if (bond.owner.toLowerCase() !== address.toLowerCase()) {
              console.log('Skipping bond not owned by user:', {
                bondId,
                owner: bond.owner,
                userAddress: address
              });
              continue;
            }

            try {
              console.log('Fetching details for bond:', {
                bondId,
                productAddress: product.address
              });

              // Get bond details with proper typing
              const bondDetails = (await publicClient.readContract({
                address: product.address,
                abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
                functionName: 'getBondDetails',
                args: [Number(bondId)]
              })) as unknown as BondDetailsResult;

              // Verify current ownership
              const currentOwner = bondDetails[1].toLowerCase();
              if (currentOwner !== address.toLowerCase()) {
                console.log('Skipping bond with different current owner:', {
                  bondId,
                  currentOwner,
                  userAddress: address
                });
                continue;
              }

              console.log('Bond details received:', {
                id: Number(bondDetails[0]),
                owner: bondDetails[1],
                asset: bondDetails[2],
                amount: bondDetails[3].toString(),
                createdAt: bondDetails[4].toString(),
                returnPercentage: Number(bondDetails[5]),
                termInSeconds: Number(bondDetails[6]),
                status: Number(bondDetails[7]),
                name: bondDetails[8],
                resellPrice: bondDetails[9].toString(),
                redemptionFee: Number(bondDetails[11]),
                reclaimFee: Number(bondDetails[15])
              });

              const processedBond: Bond = {
                id: Number(bondDetails[0]),
                owner: bondDetails[1],
                asset: bondDetails[2],
                amount: bondDetails[3],
                createdAt: bondDetails[4],
                returnPercentage: Number(bondDetails[5]),
                termInSeconds: Number(bondDetails[6]),
                status: Number(bondDetails[7]),
                name: bondDetails[8],
                resellPrice: bondDetails[9],
                redemptionFee: Number(bondDetails[11]),
                reclaimFee: Number(bondDetails[15]),
                productAddress: product.address,
                productName: product.name
              };

              // Check if bond should be included based on its status
              const shouldInclude = bond.status === ProductStatus.Active || 
                                  bond.status === ProductStatus.Created || 
                                  bond.status === ProductStatus.Reselling ||
                                  bond.status === ProductStatus.Purchased;

              console.log('Bond status check:', {
                bondId,
                status: bond.status,
                shouldInclude
              });

              if (shouldInclude) {
                processedBonds.push(processedBond);
                console.log('Added bond to processed bonds:', processedBond);
              }
            } catch (error) {
              console.error(`Error fetching details for bond ${bondId} from product ${product.name}:`, error);
            }
          }
        } catch (error) {
          console.error(`Error processing product ${product.name}:`, error);
        }
      }

      console.log('Final processed bonds:', processedBonds);
      setBonds(processedBonds);
    } catch (error) {
      console.error('Error fetching bonds:', error);
      setBondError(error as Error);
    } finally {
      setIsLoadingBonds(false);
    }
  };

  // Fetch bonds when dependencies change
  useEffect(() => {
    if (address && chain?.id === CHAIN_ID && entities) {
      fetchBondsRef.current?.();
    }
  }, [address, chain?.id, entities, bondProducts, publicClient]);

  // Handler functions
  const handleApprove = () => {
    if (approveCoupon) {
      setIsApprovingCoupon(true);
      approveCoupon();
    }
  };

  const handleRedeem = (bondId: number, productAddress: string) => {
    setSelectedBondKey(getBondKey(bondId, productAddress));
    setIsRedeeming(true);
    redeem?.();
  };

  const handleReclaim = async (bond: Bond) => {
    if (!bond || !bond.productAddress || !walletClient) return;
    
    try {
      // Get the coupon token address from the contract
      const couponTokenAddress = await publicClient.readContract({
        address: bond.productAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'couponToken'
      }) as `0x${string}`;

      // Check if we need to approve coupon tokens
      const allowance = await publicClient.readContract({
        address: couponTokenAddress,
        abi: CONFIG.ERC20_ABI.abi as Abi,
        functionName: 'allowance',
        args: [address as `0x${string}`, bond.productAddress]
      }) as bigint;

      const metrics = calculateBondMetrics(bond);
      
      if (allowance < metrics.couponAmount) {
        setIsApprovingCoupon(true);
        try {
          const { request } = await publicClient.simulateContract({
            address: couponTokenAddress,
            abi: CONFIG.ERC20_ABI.abi as Abi,
            functionName: 'approve',
            args: [bond.productAddress, metrics.couponAmount],
            account: address
          });
          
          const hash = await walletClient.writeContract(request);
          await publicClient.waitForTransactionReceipt({ hash });
        } finally {
          setIsApprovingCoupon(false);
        }
      }

      // Add bond ID to reclaiming set
      reclaimingBondIds.add(bond.id);

      // Call reclaim on the bond contract
      const { request } = await publicClient.simulateContract({
        address: bond.productAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'reclaimBond',
        args: [BigInt(bond.id)],
        account: address
      });
      
      const hash = await walletClient.writeContract(request);
      await publicClient.waitForTransactionReceipt({ hash });

      // Refresh the bonds list
      fetchBondsRef.current?.();
    } catch (error) {
      console.error('Error reclaiming bond:', error);
    } finally {
      // Remove bond ID from reclaiming set
      reclaimingBondIds.delete(bond.id);
    }
  };

  const handleRename = async (bondId: number, productAddress: string) => {
    console.log('Rename clicked:', { bondId, productAddress });
    // Ensure productAddress is a valid hex address
    if (!productAddress.startsWith('0x')) {
      console.error('Invalid product address format');
      return;
    }
    const key = `${productAddress}:${bondId}`;
    console.log('Setting bond key:', key);
    // Fetch current description before opening modal
    await fetchProductDescription(productAddress);
    setSelectedBondKey(key);
    setIsRenameModalOpen(true);
  };

  const handleResell = (bondId: number, productAddress: string) => {
    console.log('Resell clicked:', { bondId, productAddress });
    // Ensure productAddress is a valid hex address
    if (!productAddress.startsWith('0x')) {
      console.error('Invalid product address format');
      return;
    }
    const key = `${productAddress}:${bondId}`;
    console.log('Setting bond key:', key);
    setSelectedBondKey(key);
    setIsModalOpen(true);
  };

  const handleResellSubmit = () => {
    console.log('Resell submit:', {
      selectedBondKey,
      parsedKey: selectedBondKey ? parseBondKey(selectedBondKey) : null,
      resellPrice,
      writeResellPrice: !!writeResellPrice
    });
    if (!selectedBondKey || !writeResellPrice) return;
    writeResellPrice();
  };

  const handleCloseRenameModal = () => {
    setIsRenameModalOpen(false);
    setSelectedBondKey(null);
    setNewBondName('');
    setCurrentDescription('');
  };

  const handleCloseResellModal = () => {
    setIsModalOpen(false);
    setSelectedBondKey(null);
    setResellPrice('');
  };

  // Update the calculateBondMetrics function to fix days left calculation
  const calculateBondMetrics = (details: Bond | undefined) => {
    if (!details) {
      return {
        daysLeft: 0,
        redeemableAmount: 0,
        reclaimAmount: 0,
        isMature: false,
        isInRedemptionPeriod: false,
        reclaimFeeAmount: 0,
        reclaimFee: 0,
        couponAmount: 0n
      };
    }

    const now = Math.floor(Date.now() / 1000);
    const maturityDate = Number(details.createdAt) + Number(details.termInSeconds);
    const daysLeft = Math.max(0, Math.floor((maturityDate - now) / 86400));
    const isMature = now >= maturityDate;
    const isInRedemptionPeriod = now <= maturityDate;
    
    // Calculate amounts using the same logic as the contract
    const principal = BigInt(details.amount);
    const returnPercentage = BigInt(details.returnPercentage);
    const bondDebt = (principal * returnPercentage) / 10000n;
    const redemptionFeeAmount = (bondDebt * BigInt(details.redemptionFee)) / 10000n;
    
    // For reclaim, we need to approve: (amount * returnPercentage) + amount - redemption_fee_amount
    const couponAmount = ((principal * returnPercentage) / 10000n) + principal - redemptionFeeAmount;
    
    // Calculate reclaim fee
    const reclaimFee = Number(details.reclaimFee);
    const reclaimFeeAmount = Math.floor(Number(formatEther(principal)) * (reclaimFee / 10000));
    const reclaimAmount = Number(formatEther(principal)) - reclaimFeeAmount;

    return {
      daysLeft,
      redeemableAmount: Number(formatEther(principal)) + Number(formatEther(bondDebt)) - Number(formatEther(redemptionFeeAmount)),
      reclaimAmount,
      isMature,
      isInRedemptionPeriod,
      reclaimFeeAmount,
      reclaimFee,
      couponAmount
    };
  };

  // Add function to fetch detailed bond information
  const fetchBondDetails = useCallback(async (bondId: number, productAddress: `0x${string}`) => {
    if (!productAddress) return;
    
    setIsLoadingBondDetails(true);
    try {
      // Get bond details from the specific product contract
      const details = await publicClient.readContract({
        address: productAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'getBondDetails',
        args: [bondId],
      });
      
      console.log('Bond details from contract:', details);
      setSelectedBondDetails(details);
    } catch (error) {
      console.error('Error fetching bond details:', error);
      setSelectedBondDetails(null);
    } finally {
      setIsLoadingBondDetails(false);
    }
  }, [publicClient]);

  // Update useEffect to fetch bond details when info modal is opened
  useEffect(() => {
    if (showInfoModal !== null) {
      const bond = bonds.find(b => getBondKey(b.id, b.productAddress) === showInfoModal);
      if (bond) {
        fetchBondDetails(bond.id, bond.productAddress);
      }
    } else {
      setSelectedBondDetails(null);
    }
  }, [showInfoModal, bonds, fetchBondDetails]);

  // Helper to parse bond key
  const parseBondKey = (key: string): { bondId: number; productAddress: `0x${string}` } | null => {
    try {
      const [productAddress, bondId] = key.split(':');
      if (!productAddress.startsWith('0x')) {
        console.error('Invalid product address format');
        return null;
      }
      return {
        bondId: Number(bondId),
        productAddress: productAddress as `0x${string}`
      };
    } catch (e) {
      console.error('Error parsing bond key:', e);
      return null;
    }
  };

  // Update the resell config
  const { config: resellConfig } = usePrepareContractWrite({
    address: selectedBondKey ? parseBondKey(selectedBondKey)?.productAddress : undefined,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'setItemResellPrice',
    args: selectedBondKey && resellPrice ? [parseBondKey(selectedBondKey)?.bondId, parseUnits(resellPrice, 18)] : undefined,
    enabled: selectedBondKey !== null && !!resellPrice && address && chain?.id === CHAIN_ID,
  });

  // Update the rename config
  const { config: renameConfig } = usePrepareContractWrite({
    address: selectedBondKey ? parseBondKey(selectedBondKey)?.productAddress : undefined,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'setItemName',
    args: selectedBondKey && newBondName ? [parseBondKey(selectedBondKey)?.bondId, newBondName] : undefined,
    enabled: selectedBondKey !== null && newBondName !== '' && address && chain?.id === CHAIN_ID,
  });

  // Add debug logging for contract writes
  useEffect(() => {
    if (selectedBondKey) {
      const parsed = parseBondKey(selectedBondKey);
      console.log('Contract Write Debug:', {
        selectedBondKey,
        parsed,
        resellConfig: {
          address: parsed?.productAddress,
          args: parsed && resellPrice ? [parsed.bondId, resellPrice] : undefined,
          enabled: selectedBondKey !== null && !!resellPrice && address && chain?.id === CHAIN_ID
        },
        renameConfig: {
          address: parsed?.productAddress,
          args: parsed && newBondName ? [newBondName, currentDescription] : undefined,
          enabled: selectedBondKey !== null && newBondName !== '' && address && chain?.id === CHAIN_ID
        }
      });
    }
  }, [selectedBondKey, resellPrice, newBondName, currentDescription, address, chain?.id]);

  // Update the setProductAttribute config
  const { config: setProductAttributeConfig } = usePrepareContractWrite({
    address: selectedBondKey ? parseBondKey(selectedBondKey)?.productAddress : ADDRESSES.TRINITY_BOND,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'setProductAttribute',
    args: selectedBondKey && propertyKey && propertyValue ? [parseBondKey(selectedBondKey)?.bondId, propertyKey, propertyValue] : undefined,
    enabled: Boolean(selectedBondKey !== null && propertyKey && propertyValue && isAdmin),
  });

  const { write: rename } = useContractWrite({
    ...renameConfig,
    onSuccess: () => {
      setIsRenameModalOpen(false);
      setSelectedBondKey(null);
      setNewBondName('');
      setCurrentDescription('');
      fetchBondsRef.current?.();
    },
  });

  const { write: writeResellPrice } = useContractWrite({
    ...resellConfig,
    onSuccess: () => {
      setIsModalOpen(false);
      setSelectedBondKey(null);
      setResellPrice('');
      fetchBondsRef.current?.();
    },
  });

  // Add a function to generate unique bond key
  const getBondKey = (bondId: number, productAddress: string) => {
    return `${productAddress}-${bondId}`;
  };

  useEffect(() => {
    console.log('State changed:', { 
      selectedBondKey,
      parsedKey: selectedBondKey ? parseBondKey(selectedBondKey) : null,
      isModalOpen, 
      isRenameModalOpen,
      resellPrice,
      newBondName,
      renameEnabled: selectedBondKey !== null && newBondName !== '' && address && chain?.id === CHAIN_ID
    });
  }, [selectedBondKey, isModalOpen, isRenameModalOpen, resellPrice, newBondName, address, chain?.id]);

  // Add function to fetch current description
  const fetchProductDescription = async (productAddress: string) => {
    try {
      const description = await publicClient.readContract({
        address: productAddress as `0x${string}`,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'description'
      }) as string;
      console.log('Fetched description:', description);
      setCurrentDescription(description);
    } catch (error) {
      console.error('Error fetching description:', error);
      setCurrentDescription(''); // Set empty string as fallback
    }
  };

  if (!address) {
    return (
      <div className="text-center py-10">
        Connect your wallet to view your Account.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-0 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">My Account</h1>
        <p className="text-base text-gray-400/40">View your products and manage your account</p>

      {/* Entity Carousel with Products */}
      <div className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-purple-500/20 via-indigo-500/20 to-blue-500/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Entities</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
              disabled={currentSlide === 0}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CaretLeft size={20} />
            </button>
            <button
              onClick={() => setCurrentSlide(Math.min((entities?.filter(entity => entity.owner?.toLowerCase() === address?.toLowerCase()).length || 1) - 1, currentSlide + 1))}
              disabled={currentSlide >= (entities?.filter(entity => entity.owner?.toLowerCase() === address?.toLowerCase()).length || 1) - 1}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CaretRight size={20} />
            </button>
          </div>
        </div>

        <div className="relative h-96 overflow-hidden">
          {entities
            ?.filter(entity => entity.owner?.toLowerCase() === address?.toLowerCase())
            .map((entity, index) => (
              <motion.div
                key={entity.address}
                className="absolute inset-0 w-full h-full flex flex-col overflow-hidden"
                initial={{ x: index === 0 ? 0 : '100%' }}
                animate={{ 
                  x: index === currentSlide ? '0%' : (index < currentSlide ? '-100%' : '100%')
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <div className="w-full h-full flex flex-col relative overflow-hidden rounded-2xl">
                  {/* Background Image Container */}
                  <div className="absolute inset-0 w-full h-full overflow-hidden">
                    <EntityBackgroundImage entity={entity} />
                  </div>
                  
                  {/* Entity Card with Products Integrated */}
                  <div className="w-full max-w-4xl mx-auto h-full relative z-10">
                    <EntityCard 
                      entity={entity} 
                      clickable={true}
                      showProducts={true}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        {/* Dots Indicator */}
        {entities?.filter(entity => entity.owner?.toLowerCase() === address?.toLowerCase()).length > 1 && (
          <div className="flex justify-center mt-4">
            <div className="flex space-x-2">
              {entities
                ?.filter(entity => entity.owner?.toLowerCase() === address?.toLowerCase())
                .map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`cursor-pointer rounded-full transition-all duration-200 ${
                      index === currentSlide ? 'bg-white' : 'bg-white/30'
                    }`}
                    style={{
                      width: '12px',
                      height: '12px'
                    }}
                  />
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="md:col-span-1">
          <AssetsList />
        </div>
        <div className="md:col-span-1">
          <motion.div
            className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-blue-500/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-2 text-white">Purchased Items</h2>
            <div className="text-white/60">
            {isLoadingBonds || isLoadingEntities ? (
                <p>Loading...</p>
            ) : bondError ? (
              <div className="text-center text-red-400">
                <p>Error loading products: {bondError.message}</p>
              </div>
            ) : bonds.length > 0 ? (
              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {bonds.map((bond) => {
                    const metrics = calculateBondMetrics(bond);
                    const bondKey = getBondKey(bond.id, bond.productAddress);
                    const isReclaimingThis = reclaimingBondIds.has(Number(bond.id));

                    return (
                      <motion.div
                        key={bondKey}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        layout
                        className="glass-panel p-3 rounded-lg relative"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-medium">
                                    {bond.name || `Bond #${bond.id}`}
                                  </h3>
                                  <Pencil
                                    size={16}
                                    weight="regular"
                                    color="#f97316"
                                    onClick={() => handleRename(bond.id, bond.productAddress)}
                                    className="cursor-pointer"
                                  />
                                </div>
                                <p className="text-sm text-white/60">
                                  ({bond.productName})
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 text-xxs space-y-0.5">
                              <div className="grid text-xs grid-cols-2 gap-x-4 gap-y-0.5 text-gray-400">
                                <div>Amount: <span className="text-xs text-gray-400">{formatTokenAmount(bond.amount)}</span></div>
                                <div>Return: <span className="text-xs text-gray-400">{(Number(bond.returnPercentage)/100).toFixed(0)}%</span></div>
                                <div>Term: <span className="text-xs text-gray-400">{Number(bond.termInSeconds) / 86400} days</span></div>
                                <div>Created: <span className="text-xs text-gray-400">{new Date(Number(bond.createdAt) * 1000).toLocaleDateString()}</span></div>
                                <div>Days Left: <span className="text-xs text-gray-400">{metrics.daysLeft}</span></div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1 min-w-[100px]">
                            <button
                              onClick={() => setShowInfoModal(bondKey)}
                              className="p-1 rounded-full hover:bg-white/10 transition-colors"
                            >
                              <Info size={16} className="text-white/60" />
                            </button>

                            {metrics.couponAmount > 0n && (
                              <button
                                onClick={() => handleReclaim(bond)}
                                disabled={isApprovingCoupon || reclaimingBondIds.has(bond.id)}
                                className="px-3 py-1 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isApprovingCoupon ? "Approving..." : "Reclaim"}
                              </button>
                            )}

                            <div className="text-right">
                              <div className="text-xs text-white/50">Early Redemption:</div>
                              <div className="font-medium text-xs">{Math.floor(metrics.reclaimAmount).toLocaleString()}</div>
                            </div>

                              <div className="text-right mb-3">
                              <div className="text-xs text-white/50">Full Redemption:</div>
                              <div className="font-medium text-xs">{Math.floor(metrics.redeemableAmount).toLocaleString()}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="absolute bottom-2 right-2">
                          {bond.resellPrice > 0n && (
                            <div className="flex items-center gap-1 text-yellow-400">
                              <Tag 
                              weight="fill" 
                              size={16} 
                              color="yellow" 
                              onClick={() => handleResell(bond.id, bond.productAddress)} className="cursor-pointer hover:opacity-80 transition-opacity" />
                              <span className="text-xs text-yellow-400">
                                {formatTokenAmount(bond.resellPrice)} †
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="absolute bottom-2 right-2">
                        {bond.resellPrice <= 0n && (
                          <Tag
                            size={16}
                            weight="regular"
                            color="#10b981"
                            onClick={() => handleResell(bond.id, bond.productAddress)}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          />
                        )}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : (
                <p className="text-center text-secondary">No Items found</p>
            )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Resell Modal */}
      <AnimatePresence>
        {isModalOpen && selectedBondKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={handleCloseResellModal}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-gradient-to-br from-purple-500/10 via-indigo-500/10 to-blue-500/10 p-6 rounded-xl max-w-md w-full mx-4"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Resell Bond #{parseBondKey(selectedBondKey)?.bondId}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Resell Price (TRNTY)</label>
                  <input
                    type="number"
                    value={resellPrice}
                    onChange={(e) => setResellPrice(e.target.value)}
                    className="w-full px-3 py-2 bg-black/20 rounded-lg border border-white/10 focus:border-blue-500/50 focus:outline-none"
                    placeholder="Enter price in TRNTY"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleCloseResellModal}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResellSubmit}
                    disabled={!writeResellPrice}
                    className="px-4 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    List for Sale
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rename Modal */}
      <AnimatePresence>
        {isRenameModalOpen && selectedBondKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={handleCloseRenameModal}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-gradient-to-br from-purple-500/10 via-indigo-500/10 to-blue-500/10 p-6 rounded-xl max-w-md w-full mx-4"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Rename Bond #{parseBondKey(selectedBondKey)?.bondId}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">New Name</label>
                  <input
                    type="text"
                    value={newBondName}
                    onChange={(e) => setNewBondName(e.target.value)}
                    className="w-full px-3 py-2 bg-black/20 rounded-lg border border-white/10 focus:border-blue-500/50 focus:outline-none"
                    placeholder="Enter new name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleCloseRenameModal}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => rename?.()}
                    disabled={!rename || !newBondName.trim()}
                    className="px-4 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Rename
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {showInfoModal !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowInfoModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-gray-900/95 p-6 rounded-xl max-w-md mx-4 shadow-xl border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              {(() => {
                const bond = bonds.find(b => getBondKey(b.id, b.productAddress) === showInfoModal);
                if (!bond) return null;
                
                const metrics = calculateBondMetrics(bond);
                return (
                  <div key={getBondKey(bond.id, bond.productAddress)}>
                    <h3 className="text-xl font-bold mb-4 text-white">
                      {bond.name || `Bond #${bond.id}`} Details
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <div className="text-white/60 text-sm">Product Address</div>
                        <div className="font-mono text-sm text-white/80 break-all">
                          {bond.productAddress}
                        </div>
                      </div>

                      <div>
                        <div className="text-white/60 text-sm">Bond ID</div>
                        <div className="text-white/80">{bond.id}</div>
                      </div>

                      {isLoadingBondDetails ? (
                        <div className="text-center py-4">
                          <p className="text-white/60">Loading details...</p>
                        </div>
                      ) : selectedBondDetails ? (
                        <>
                          <div>
                            <div className="text-white/60 text-sm">Amount</div>
                            <div className="text-white/80">
                              {formatTokenAmount(bond.amount)}
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Return Percentage</div>
                            <div className="text-white/80">
                              {(Number(bond.returnPercentage)/100)}%
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Term</div>
                            <div className="text-white/80">
                              {Number(bond.termInSeconds) / 86400} days
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Created</div>
                            <div className="text-white/80">
                              {new Date(Number(bond.createdAt) * 1000).toLocaleDateString()}
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Days Left</div>
                            <div className="text-white/80">{metrics.daysLeft}</div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Status</div>
                            <div className="text-white/80">
                              {bond.status === 1 ? 'Active' : bond.status === 2 ? 'Redeemed' : bond.status === 3 ? 'Reclaimed' : bond.status === 4 ? 'Inactive' : bond.status === 5 ? 'Expired' : 'Unknown'}
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Redemption Fee</div>
                            <div className="text-white/80">
                              {(Number(bond.redemptionFee)/100)}%
                            </div>
                          </div>

                          <div>
                            <div className="text-white/60 text-sm">Reclaim Fee</div>
                            <div className="text-white/80">
                              {(Number(bond.reclaimFee)/100)}%
                            </div>
                          </div>

                          {selectedBondDetails[9] && (
                            <div>
                              <div className="text-white/60 text-sm">Resell Price</div>
                              <div className="text-white/80">
                                {formatTokenAmount(selectedBondDetails[9])}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-red-400">Error loading product details</p>
                        </div>
                      )}
                    </div>

                    <button
                      className="mt-6 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-white/80 hover:text-white w-full"
                      onClick={() => setShowInfoModal(null)}
                    >
                      Close
                    </button>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}